import React from "react";
import styled, { keyframes } from "styled-components";

import * as colors from "../../styles/colors";

// 10-20 system electrode coordinates in a 220x235 SVG viewBox
// Head is centered at (110, 128) with nose pointing up (y=0 is top)
const POSITIONS = {
  Fp1: [92, 45],
  Fp2: [128, 45],
  AF7: [72, 58],
  AF3: [96, 60],
  AFz: [110, 60],
  AF4: [124, 60],
  AF8: [148, 58],
  F7: [58, 78],
  F5: [74, 82],
  F3: [92, 85],
  Fz: [110, 87],
  F4: [128, 85],
  F6: [146, 82],
  F8: [162, 78],
  FT7: [46, 102],
  FC5: [69, 104],
  FC3: [92, 105],
  FCz: [110, 107],
  FC4: [128, 105],
  FC6: [151, 104],
  FT8: [174, 102],
  T7: [38, 128],
  C3: [92, 128],
  Cz: [110, 128],
  C4: [128, 128],
  T8: [182, 128],
  TP7: [46, 152],
  CP3: [92, 150],
  CPz: [110, 150],
  CP4: [128, 150],
  TP8: [174, 152],
  TP9: [30, 166],
  TP10: [190, 166],
  P7: [58, 175],
  P3: [92, 175],
  Pz: [110, 177],
  P4: [128, 175],
  P8: [162, 175],
  PO7: [72, 192],
  PO3: [95, 195],
  POz: [110, 197],
  PO4: [125, 195],
  PO8: [148, 192],
  O1: [96, 212],
  Oz: [110, 214],
  O2: [124, 212],
};

// Per-electrode signal pickup radius in SVG units (~3 cm on scalp)
const PICKUP_RADIUS = 32;

// Temporarily hiding lobe region overlays; blast radii already communicate coverage
const SHOW_LOBE_REGIONS = false;

// Lobe regions for translucent highlight overlays (approximate scalp zones)
const LOBE_REGIONS = {
  frontal: "M 50 60 Q 110 38 170 60 Q 175 85 155 98 Q 110 92 65 98 Q 45 85 50 60 Z",
  central: "M 40 105 Q 110 95 180 105 Q 185 128 180 150 Q 110 145 40 150 Q 35 128 40 105 Z",
  parietal: "M 50 155 Q 110 148 170 155 Q 165 180 155 190 Q 110 182 65 190 Q 55 180 50 155 Z",
  temporal: "M 28 115 Q 20 135 28 168 L 58 168 Q 48 135 58 115 Z M 192 115 Q 200 135 192 168 L 162 168 Q 172 135 162 115 Z",
  occipital: "M 70 195 Q 110 188 150 195 Q 148 212 135 220 Q 110 218 85 220 Q 72 212 70 195 Z",
};

const Figure = styled.figure`
  margin: 28px auto;
  padding: 0;
  max-width: 100%;
  width: 100%;
`;

const Container = styled.div`
  background: #0c0f14;
  border-radius: 12px;
  padding: 24px;
  display: flex;
  align-items: stretch;

  @media (max-width: 820px) {
    flex-direction: column;
    padding: 16px;
    gap: 32px;
  }
`;

const Column = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 14px;
  padding: 0 24px;

  @media (max-width: 820px) {
    padding: 0;
  }
`;

const ColumnHeader = styled.div`
  text-align: center;
  margin-bottom: 6px;
`;

const ColumnTitle = styled.div`
  font-size: 32px;
  font-weight: 700;
  color: ${(p) => p.$color || "#cbd5e1"};
  line-height: 1.2;
  margin-bottom: 3px;

  @media (max-width: 820px) {
    font-size: 20px;
  }
`;

const ColumnSubtitle = styled.div`
  font-size: 13px;
  font-style: italic;
  color: ${(p) => p.$color || "#94a3b8"};
  opacity: ${(p) => p.$opacity || 1};
`;

const HeadWrap = styled.div`
  background: #11151b;
  border: 1px solid #2a3340;
  border-radius: 10px;
  padding: 12px;
`;

const LobeLegend = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  background: #11151b;
  border: 1px solid #2a3340;
  border-radius: 8px;
  padding: 10px 14px;
`;

const LobeRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 12px;
  color: #cbd5e1;
  line-height: 1.4;
`;

const LobeLabel = styled.span`
  font-weight: 600;
  color: #cbd5e1;
`;

const LobeStatus = styled.span`
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.5px;
  color: ${(p) => p.$color};
`;

const PositionRow = styled.div`
  background: #11151b;
  border: 1px solid #2a3340;
  border-radius: 8px;
  padding: 10px 14px;
  font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
  font-size: 12px;
  color: #cbd5e1;
  text-align: center;
  letter-spacing: 0.5px;
`;

const pulse = keyframes`
  0%, 100% {
    opacity: 0.35;
  }
  50% {
    opacity: 1;
  }
`;

const BlastCircle = styled.circle`
  animation: ${pulse} 3.2s ease-in-out infinite;

  @media (prefers-reduced-motion: reduce) {
    animation: none;
    opacity: 0.7;
  }
`;

const ChannelPill = styled.div`
  display: inline-block;
  padding: 4px 14px 2px;
  border-radius: 999px;
  background: ${(p) => p.$bg || "#1c2028"};
  color: ${(p) => p.$color || "#cbd5e1"};
  border: 1px solid ${(p) => p.$border || "#3f4651"};
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 1px;
  line-height: 1.5;
  margin: 0 auto;
`;

const ChannelPillWrap = styled.div`
  text-align: center;
`;

const Caption = styled.figcaption`
  margin-top: 12px;
  padding: 0 4px;
  font-size: 14px;
  line-height: 1.6;
  color: ${colors.muted};
  text-align: center;
  font-style: italic;

  @media (max-width: 640px) {
    font-size: 13px;
    text-align: left;
    margin-top: 10px;
    padding: 0;
  }
`;

function HeadDiagram({ activePositions, activeColor, activeGlow, lobesCovered, clipId }) {
  const activeSet = new Set(activePositions);
  return (
    <svg
      viewBox="0 0 220 245"
      xmlns="http://www.w3.org/2000/svg"
      role="img"
      aria-hidden="true"
      style={{ width: "100%", height: "auto", display: "block" }}
    >
      <defs>
        {/* Clip the pickup-radius circles to the scalp area so coverage doesn't bleed past the head */}
        <clipPath id={clipId}>
          <ellipse cx="110" cy="128" rx="92" ry="108" />
        </clipPath>
      </defs>

      {/* Nose */}
      <path
        d="M 104 20 Q 110 8 116 20"
        fill="none"
        stroke="#475569"
        strokeWidth="1.5"
        strokeLinecap="round"
      />

      {/* Ears */}
      <ellipse cx="22" cy="128" rx="6" ry="14" fill="none" stroke="#475569" strokeWidth="1.5" />
      <ellipse cx="198" cy="128" rx="6" ry="14" fill="none" stroke="#475569" strokeWidth="1.5" />

      {/* Head outline */}
      <ellipse cx="110" cy="128" rx="92" ry="108" fill="#0c0f14" stroke="#475569" strokeWidth="1.5" />

      {/* Lobe region highlights (translucent) */}
      {SHOW_LOBE_REGIONS &&
        Object.keys(LOBE_REGIONS).map((lobe) =>
          lobesCovered[lobe] ? (
            <path
              key={lobe}
              d={LOBE_REGIONS[lobe]}
              fill={activeColor}
              fillOpacity={lobesCovered[lobe] === "partial" ? 0.06 : 0.1}
              stroke="none"
            />
          ) : null
        )}

      {/* Per-electrode pickup-radius coverage (clipped to the scalp) */}
      <g clipPath={`url(#${clipId})`}>
        {activePositions.map((pos) => {
          const [x, y] = POSITIONS[pos];
          return (
            <BlastCircle
              key={`radius-${pos}`}
              cx={x}
              cy={y}
              r={PICKUP_RADIUS}
              fill={activeColor}
              fillOpacity="0.12"
              stroke="none"
            />
          );
        })}
      </g>

      {/* Context (inactive) electrode dots */}
      {Object.keys(POSITIONS).map((pos) => {
        if (activeSet.has(pos)) return null;
        const [x, y] = POSITIONS[pos];
        return <circle key={pos} cx={x} cy={y} r="2" fill="#2a3340" />;
      })}

      {/* Active electrode dots with labels */}
      {activePositions.map((pos) => {
        const [x, y] = POSITIONS[pos];
        // Offset labels below/above depending on vertical position
        const labelY = y > 128 ? y + 12 : y - 6;
        return (
          <g key={pos}>
            <circle
              cx={x}
              cy={y}
              r="6"
              fill={activeColor}
              stroke={activeGlow}
              strokeWidth="2"
              opacity="0.95"
            />
            <circle cx={x} cy={y} r="3" fill="#ffffff" fillOpacity="0.9" />
            <text
              x={x}
              y={labelY}
              textAnchor="middle"
              fontSize="9"
              fontWeight="700"
              fill={activeColor}
              fontFamily="ui-monospace, SFMono-Regular, Menlo, monospace"
            >
              {pos}
            </text>
          </g>
        );
      })}
    </svg>
  );
}

function LobeCoverage({ lobes, color }) {
  const items = [
    { key: "frontal", label: "Frontal" },
    { key: "central", label: "Central" },
    { key: "parietal", label: "Parietal" },
    { key: "occipital", label: "Occipital" },
    { key: "temporal", label: "Temporal" },
  ];
  return (
    <LobeLegend>
      {items.map(({ key, label }) => {
        const state = lobes[key];
        let statusText = "No coverage";
        let statusColor = "#64748b";
        if (state === true) {
          statusText = "Covered";
          statusColor = color;
        } else if (state === "partial") {
          statusText = "Partial";
          statusColor = "#f59e0b";
        }
        return (
          <LobeRow key={key}>
            <LobeLabel>{label}</LobeLabel>
            <LobeStatus $color={statusColor}>{statusText}</LobeStatus>
          </LobeRow>
        );
      })}
    </LobeLegend>
  );
}

export function SensorPlacementDiagram({ left, right, caption }) {
  return (
    <Figure>
      <Container>
        {/* Left device */}
        <Column>
          <ColumnHeader>
            <ColumnTitle $color={left.titleColor || "#cbd5e1"}>{left.name}</ColumnTitle>
            <ColumnSubtitle $color={left.titleColor || "#94a3b8"}>
              {left.subtitle}
            </ColumnSubtitle>
          </ColumnHeader>

          <HeadWrap>
            <HeadDiagram
              activePositions={left.positions}
              activeColor={left.color}
              activeGlow={left.glow || left.color}
              lobesCovered={left.lobes}
              clipId="sensor-clip-left"
            />
          </HeadWrap>

          <ChannelPillWrap>
            <ChannelPill $color={left.color} $border={left.color} $bg="#11151b">
              {left.positions.length} {left.positions.length === 1 ? "channel" : "channels"}
            </ChannelPill>
          </ChannelPillWrap>

          <LobeCoverage lobes={left.lobes} color={left.color} />

          <PositionRow>{left.positions.join(" · ")}</PositionRow>
        </Column>

        {/* Right device */}
        <Column>
          <ColumnHeader>
            <ColumnTitle $color={right.titleColor || "#a5b4fc"}>{right.name}</ColumnTitle>
            <ColumnSubtitle $color={right.titleColor || "#a5b4fc"} $opacity={0.75}>
              {right.subtitle}
            </ColumnSubtitle>
          </ColumnHeader>

          <HeadWrap>
            <HeadDiagram
              activePositions={right.positions}
              activeColor={right.color}
              activeGlow={right.glow || right.color}
              lobesCovered={right.lobes}
              clipId="sensor-clip-right"
            />
          </HeadWrap>

          <ChannelPillWrap>
            <ChannelPill $color={right.color} $border={right.color} $bg="#11151b">
              {right.positions.length} {right.positions.length === 1 ? "channel" : "channels"}
            </ChannelPill>
          </ChannelPillWrap>

          <LobeCoverage lobes={right.lobes} color={right.color} />

          <PositionRow>{right.positions.join(" · ")}</PositionRow>
        </Column>
      </Container>
      {caption && <Caption>{caption}</Caption>}
    </Figure>
  );
}
